import { type CalendarDate, type DateValue, type Time } from '@internationalized/date';
import clsx from 'clsx';
import React from 'react';
import { Calendar, Dialog as AriaDialog, I18nProvider, Popover } from 'react-aria-components';
import InputText from '../../../../atoms/InputText/InputText';
import CalendarContent from '../CalendarContent/CalendarContent';
import HourField from '../HourField/HourField';

/**
 * DatePickerShell — the shared, presentational shell of the popover date pickers (`DatePicker` and
 * `DatePickerActions`). It renders the read-only `InputText` trigger and the `Popover` (react-aria `Calendar`
 * via `CalendarContent`, optional segmented `TimeField`, plus an `extraActions` slot for extra controls and an
 * `actionsElement` slot for action buttons). It owns no value/commit logic: the parent passes the draft (`dateValue`/`timeValue`)
 * and handlers, plus the already-formatted `displayValue`. Themed with the `.date-picker-popup` `--ui-*` classes.
 *
 * It does centralize one piece of behavior: a non-modal `Popover` does not dismiss on an outside pointer down,
 * so the shell restores that by routing outside clicks through `onOpenChange(false)` — letting each parent decide
 * the close semantics (plain close vs revert).
 */

type DatePickerShellProps = {
  accessibleName: string;
  dateValue: CalendarDate | null;
  disabled?: boolean;
  // Already locale-formatted text shown in the read-only trigger.
  displayValue: string;
  // Action buttons row (Clear/Cancel/Save); rendered below the calendar/time.
  actionsElement?: React.ReactNode;
  // Extra controls shown in the same centered row as the TimeField (e.g. a deliver-late checkbox).
  extraActions?: React.ReactNode;
  // Shows a native year <select> in the calendar header (month stays on prev/next). Forwarded to CalendarContent.
  hasYearPicker?: boolean;
  // Marks individual days as unavailable (e.g. weekends); passed straight to react-aria's Calendar.
  isDateUnavailable?: (date: DateValue) => boolean;
  isLabelHidden?: boolean;
  isNonModal?: boolean;
  isOpen: boolean;
  label?: string;
  locale: string;
  maxValue?: DateValue;
  minValue?: DateValue;
  // When provided and the field has a value, a clickable clear (×) appears on the right of the trigger (next
  // to the calendar icon on the left); clicking it clears the field. Used by the simple DatePicker
  // (DatePickerActions clears from its own popover button instead).
  onClear?: () => void;
  onDateChange: (date: CalendarDate) => void;
  // Trigger click (opens the popover).
  onOpen: () => void;
  // react-aria-driven open-state change (Escape / outside click). Also fed by the non-modal outside-click guard.
  onOpenChange: (nextOpen: boolean) => void;
  onTimeChange: (time: Time | null) => void;
  placeholder: string;
  showHour?: boolean;
  timeValue: Time | null;
};

function DatePickerShell({
  accessibleName,
  actionsElement,
  dateValue,
  disabled = false,
  displayValue,
  extraActions,
  hasYearPicker = false,
  isDateUnavailable,
  isLabelHidden = false,
  isNonModal = false,
  isOpen,
  label,
  locale,
  maxValue,
  minValue,
  onClear,
  onDateChange,
  onOpen,
  onOpenChange,
  onTimeChange,
  placeholder,
  showHour = false,
  timeValue,
}: DatePickerShellProps): JSX.Element {
  const triggerRef = React.useRef<HTMLInputElement>(null);

  // A non-modal popover does not dismiss on outside interaction, so restore it while open by routing the
  // outside pointer down through onOpenChange(false) (the parent decides whether that closes or also reverts).
  React.useEffect(() => {
    if (!isOpen || !isNonModal) return undefined;
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Element | null;
      if (target?.closest?.('.date-picker-popup') || target?.closest?.('.date-picker-popup__popover')) return;
      onOpenChange(false);
    }
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, isNonModal, onOpenChange]);

  // Clearable pickers keep the calendar icon on the left (it always opens the popover) and show a clickable
  // clear (×) on the right only while a value is set. Non-clearable pickers keep a single calendar icon.
  const canClear = Boolean(onClear) && !disabled;
  const showClear = canClear && Boolean(displayValue);

  return (
    <I18nProvider locale={locale}>
      <div className="date-picker-popup">
        {label && <span className={clsx('date-picker-popup__label', { 'date-picker-popup__label--hidden': isLabelHidden })}>{label}</span>}
        <InputText
          aria-label={accessibleName}
          disabled={disabled}
          icon="calendar"
          iconLeft={canClear}
          onClick={onOpen}
          onClickIcon={onOpen}
          onClickSecondaryIcon={onClear}
          placeholder={placeholder}
          readOnly
          ref={triggerRef}
          secondaryIcon={showClear ? 'close' : undefined}
          value={displayValue}
        />
        <Popover className="date-picker-popup__popover" isNonModal={isNonModal} isOpen={isOpen} onOpenChange={onOpenChange} placement="bottom start" triggerRef={triggerRef}>
          <AriaDialog className="date-picker-popup__dialog">
            <div className="date-picker-popup__dialog-calendar">
              <Calendar
                aria-label={accessibleName}
                className="month-calendar date-picker-popup__calendar"
                isDateUnavailable={isDateUnavailable}
                maxValue={maxValue}
                minValue={minValue}
                onChange={onDateChange}
                value={dateValue}
              >
                <CalendarContent hasYearPicker={hasYearPicker} locale={locale} />
              </Calendar>
              {(showHour || extraActions) && (
                <div className="date-picker-popup__bottom">
                  {showHour && <HourField onChange={onTimeChange} value={timeValue} />}
                  {extraActions}
                </div>
              )}
            </div>
            {actionsElement}
          </AriaDialog>
        </Popover>
      </div>
    </I18nProvider>
  );
}

export default DatePickerShell;
